import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Grid
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useQuery } from '@tanstack/react-query';
import appointmentsApi from '../../../api/appointments';
import { getTenantCacheKey } from '../../../utils/auth';

const AppointmentFilters = ({ onFiltersChange }) => {
  const [selectedClinicians, setSelectedClinicians] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [showUnassigned, setShowUnassigned] = useState(true); // Show unassigned by default
  const [expanded, setExpanded] = useState(false);

  // Fetch clinicians/providers with tenant-specific cache key
  const { data: cliniciansData = [] } = useQuery({
    queryKey: [getTenantCacheKey('providers')],
    queryFn: () => appointmentsApi.getProviders(),
    staleTime: 5 * 60 * 1000
  });

  // Fetch locations with tenant-specific cache key
  const { data: locationsData = [] } = useQuery({
    queryKey: [getTenantCacheKey('locations')],
    queryFn: () => appointmentsApi.getLocations(),
    staleTime: 5 * 60 * 1000
  });

  // Memoize the arrays to prevent unnecessary re-renders
  const clinicians = useMemo(() => cliniciansData, [cliniciansData]);
  const locations = useMemo(() => locationsData, [locationsData]);

  // Handle clinician filter changes
  const handleClinicianChange = (clinicianId, checked) => {
    const newSelected = checked
      ? [...selectedClinicians, clinicianId]
      : selectedClinicians.filter(id => id !== clinicianId);
    
    setSelectedClinicians(newSelected);
  };

  // Handle location filter changes
  const handleLocationChange = (locationId, checked) => {
    const newSelected = checked
      ? [...selectedLocations, locationId]
      : selectedLocations.filter(id => id !== locationId);
    
    setSelectedLocations(newSelected);
  };

  // Handle "All Clinicians" checkbox
  const handleAllCliniciansChange = (checked) => {
    if (checked) {
      setSelectedClinicians(clinicians.map(c => c.id));
    } else {
      setSelectedClinicians([]);
    }
  };

  // Handle "All Locations" checkbox
  const handleAllLocationsChange = (checked) => {
    if (checked) {
      setSelectedLocations(locations.map(l => l.id));
    } else {
      setSelectedLocations([]);
    }
  };

  // Notify parent component when filters change (use lengths to prevent infinite loops)
  useEffect(() => {
    const filters = {};

    // Only add filters if they're not "all" selected or empty
    if (selectedClinicians.length > 0 && selectedClinicians.length < clinicians.length) {
      filters.user_ids = selectedClinicians;
    }

    if (selectedLocations.length > 0 && selectedLocations.length < locations.length) {
      filters.location_ids = selectedLocations;
    }

    // Add unassigned filter
    filters.showUnassigned = showUnassigned;

    onFiltersChange(filters);
  }, [selectedClinicians, selectedLocations, showUnassigned, clinicians.length, locations.length]); // Removed onFiltersChange from dependencies

  // Check if all clinicians are selected
  const allCliniciansSelected = clinicians.length > 0 && selectedClinicians.length === clinicians.length;
  const allLocationsSelected = locations.length > 0 && selectedLocations.length === locations.length;

  return (
    <Paper sx={{ mb: 2 }}>
      <Accordion expanded={expanded} onChange={(e, isExpanded) => setExpanded(isExpanded)}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">
            Filters 
            {(selectedClinicians.length > 0 || selectedLocations.length > 0) && !allCliniciansSelected && !allLocationsSelected && (
              <Typography component="span" variant="body2" color="primary" sx={{ ml: 1 }}>
                (Active)
              </Typography>
            )}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            {/* Unassigned Appointments Toggle */}
            <Grid item xs={12}>
              <Box sx={{ mb: 2, p: 1.5, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ff9800' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showUnassigned}
                      onChange={(e) => setShowUnassigned(e.target.checked)}
                      sx={{
                        color: '#ff9800',
                        '&.Mui-checked': {
                          color: '#ff9800',
                        },
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography sx={{ fontWeight: 500 }}>
                        Show Unassigned Client Appointments
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                        (appointments with no client data in Pabau)
                      </Typography>
                    </Box>
                  }
                />
              </Box>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            {/* Clinician Filters */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
                Clinician
              </Typography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={allCliniciansSelected || selectedClinicians.length === 0}
                      indeterminate={selectedClinicians.length > 0 && selectedClinicians.length < clinicians.length}
                      onChange={(e) => handleAllCliniciansChange(e.target.checked)}
                    />
                  }
                  label="All Clinicians"
                  sx={{ mb: 1, '& .MuiFormControlLabel-label': { fontWeight: 500 } }}
                />
                <Divider sx={{ mb: 1 }} />
                {clinicians.map((clinician) => (
                  <FormControlLabel
                    key={clinician.id}
                    control={
                      <Checkbox
                        checked={selectedClinicians.includes(clinician.id)}
                        onChange={(e) => handleClinicianChange(clinician.id, e.target.checked)}
                      />
                    }
                    label={clinician.full_name || clinician.email || `Clinician ${clinician.id}`}
                    sx={{ ml: 2 }}
                  />
                ))}
              </FormGroup>
            </Grid>

            {/* Location Filters */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
                Location
              </Typography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={allLocationsSelected || selectedLocations.length === 0}
                      indeterminate={selectedLocations.length > 0 && selectedLocations.length < locations.length}
                      onChange={(e) => handleAllLocationsChange(e.target.checked)}
                    />
                  }
                  label="All Locations"
                  sx={{ mb: 1, '& .MuiFormControlLabel-label': { fontWeight: 500 } }}
                />
                <Divider sx={{ mb: 1 }} />
                {locations.map((location) => (
                  <FormControlLabel
                    key={location.id}
                    control={
                      <Checkbox
                        checked={selectedLocations.includes(location.id)}
                        onChange={(e) => handleLocationChange(location.id, e.target.checked)}
                      />
                    }
                    label={location.location_name || location.name || `Location ${location.id}`}
                    sx={{ ml: 2 }}
                  />
                ))}
              </FormGroup>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Paper>
  );
};

export default AppointmentFilters; 